import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import {
  HRContextType,
  HREmployee,
  HRDepartment,
  HRPosition,
  HRContract,
  EmployeeFormData,
  DepartmentFormData,
  PositionFormData,
  ContractFormData,
} from "../types/hr";
import { useHRAPI } from "../hooks/useHRAPI";

// ===== ACTIONS =====
type HRAction =
  // Loading states
  | { type: "SET_LOADING"; resource: string; loading: boolean }
  // Data loading
  | { type: "SET_EMPLOYEES"; employees: HREmployee[] }
  | { type: "SET_DEPARTMENTS"; departments: HRDepartment[] }
  | { type: "SET_POSITIONS"; positions: HRPosition[] }
  | { type: "SET_CONTRACTS"; contracts: HRContract[] }
  // CRUD operations
  | { type: "ADD_EMPLOYEE"; employee: HREmployee }
  | { type: "UPDATE_EMPLOYEE"; id: number; employee: Partial<HREmployee> }
  | { type: "REMOVE_EMPLOYEE"; id: number }
  | { type: "ADD_DEPARTMENT"; department: HRDepartment }
  | { type: "UPDATE_DEPARTMENT"; id: number; department: Partial<HRDepartment> }
  | { type: "REMOVE_DEPARTMENT"; id: number }
  | { type: "ADD_POSITION"; position: HRPosition }
  | { type: "UPDATE_POSITION"; id: number; position: Partial<HRPosition> }
  | { type: "REMOVE_POSITION"; id: number }
  | { type: "ADD_CONTRACT"; contract: HRContract }
  | { type: "UPDATE_CONTRACT"; id: number; contract: Partial<HRContract> }
  | { type: "REMOVE_CONTRACT"; id: number }
  // Errors
  | { type: "SET_ERROR"; resource: string; error: string | null }
  | { type: "CLEAR_ERRORS" };

// ===== STATE =====
interface HRState {
  employees: HREmployee[];
  departments: HRDepartment[];
  positions: HRPosition[];
  contracts: HRContract[];
  loading: {
    employees: boolean;
    departments: boolean;
    positions: boolean;
    contracts: boolean;
  };
  errors: {
    employees?: string;
    departments?: string;
    positions?: string;
    contracts?: string;
  };
}

const initialState: HRState = {
  employees: [],
  departments: [],
  positions: [],
  contracts: [],
  loading: {
    employees: false,
    departments: false,
    positions: false,
    contracts: false,
  },
  errors: {},
};

// ===== REDUCER =====
const hrReducer = (state: HRState, action: HRAction): HRState => {
  switch (action.type) {
    case "SET_LOADING":
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.resource]: action.loading,
        },
      };

    case "SET_EMPLOYEES":
      return {
        ...state,
        employees: action.employees,
        loading: { ...state.loading, employees: false },
      };

    case "SET_DEPARTMENTS":
      return {
        ...state,
        departments: action.departments,
        loading: { ...state.loading, departments: false },
      };

    case "SET_POSITIONS":
      return {
        ...state,
        positions: action.positions,
        loading: { ...state.loading, positions: false },
      };

    case "SET_CONTRACTS":
      return {
        ...state,
        contracts: action.contracts,
        loading: { ...state.loading, contracts: false },
      };

    case "ADD_EMPLOYEE":
      return {
        ...state,
        employees: [...state.employees, action.employee],
      };

    case "UPDATE_EMPLOYEE":
      return {
        ...state,
        employees: state.employees.map((emp) =>
          emp.id === action.id ? { ...emp, ...action.employee } : emp
        ),
      };

    case "REMOVE_EMPLOYEE":
      return {
        ...state,
        employees: state.employees.filter((emp) => emp.id !== action.id),
      };

    case "ADD_DEPARTMENT":
      return {
        ...state,
        departments: [...state.departments, action.department],
      };

    case "UPDATE_DEPARTMENT":
      return {
        ...state,
        departments: state.departments.map((dept) =>
          dept.id === action.id ? { ...dept, ...action.department } : dept
        ),
      };

    case "REMOVE_DEPARTMENT":
      return {
        ...state,
        departments: state.departments.filter((dept) => dept.id !== action.id),
      };

    case "ADD_POSITION":
      return {
        ...state,
        positions: [...state.positions, action.position],
      };

    case "UPDATE_POSITION":
      return {
        ...state,
        positions: state.positions.map((pos) =>
          pos.id === action.id ? { ...pos, ...action.position } : pos
        ),
      };

    case "REMOVE_POSITION":
      return {
        ...state,
        positions: state.positions.filter((pos) => pos.id !== action.id),
      };

    case "ADD_CONTRACT":
      return {
        ...state,
        contracts: [...state.contracts, action.contract],
      };

    case "UPDATE_CONTRACT":
      return {
        ...state,
        contracts: state.contracts.map((contract) =>
          contract.id === action.id
            ? { ...contract, ...action.contract }
            : contract
        ),
      };

    case "REMOVE_CONTRACT":
      return {
        ...state,
        contracts: state.contracts.filter(
          (contract) => contract.id !== action.id
        ),
      };

    case "SET_ERROR":
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.resource]: action.error,
        },
      };

    case "CLEAR_ERRORS":
      return {
        ...state,
        errors: {},
      };

    default:
      return state;
  }
};

// ===== CONTEXT =====
const HRContext = createContext<HRContextType | undefined>(undefined);

interface HRProviderProps {
  children: React.ReactNode;
}

export const HRProvider: React.FC<HRProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(hrReducer, initialState);
  const hrAPI = useHRAPI();

  // ===== FETCH FUNCTIONS =====

  const fetchEmployees = useCallback(async () => {
    dispatch({ type: "SET_LOADING", resource: "employees", loading: true });
    try {
      const employees = await hrAPI.getEmployees();
      if (employees) {
        dispatch({ type: "SET_EMPLOYEES", employees });
        dispatch({ type: "SET_ERROR", resource: "employees", error: null });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors du chargement des employés";
      dispatch({
        type: "SET_ERROR",
        resource: "employees",
        error: errorMessage,
      });
    }
  }, []); // Pas de dépendances pour éviter les boucles

  const fetchDepartments = useCallback(async () => {
    dispatch({ type: "SET_LOADING", resource: "departments", loading: true });
    try {
      const departments = await hrAPI.getDepartments();
      if (departments) {
        dispatch({ type: "SET_DEPARTMENTS", departments });
        dispatch({ type: "SET_ERROR", resource: "departments", error: null });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors du chargement des départements";
      dispatch({
        type: "SET_ERROR",
        resource: "departments",
        error: errorMessage,
      });
    }
  }, []); // Pas de dépendances pour éviter les boucles

  const fetchPositions = useCallback(async () => {
    dispatch({ type: "SET_LOADING", resource: "positions", loading: true });
    try {
      const positions = await hrAPI.getPositions();
      if (positions) {
        dispatch({ type: "SET_POSITIONS", positions });
        dispatch({ type: "SET_ERROR", resource: "positions", error: null });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors du chargement des postes";
      dispatch({
        type: "SET_ERROR",
        resource: "positions",
        error: errorMessage,
      });
    }
  }, []); // Pas de dépendances pour éviter les boucles

  const fetchContracts = useCallback(async () => {
    dispatch({ type: "SET_LOADING", resource: "contracts", loading: true });
    try {
      const contracts = await hrAPI.getContracts();
      if (contracts) {
        dispatch({ type: "SET_CONTRACTS", contracts });
        dispatch({ type: "SET_ERROR", resource: "contracts", error: null });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors du chargement des contrats";
      dispatch({
        type: "SET_ERROR",
        resource: "contracts",
        error: errorMessage,
      });
    }
  }, []); // Pas de dépendances pour éviter les boucles

  // ===== CRUD FUNCTIONS =====

  // Employés
  const createEmployee = useCallback(async (data: EmployeeFormData) => {
    try {
      const employee = await hrAPI.createEmployee(data);
      if (employee) {
        dispatch({ type: "ADD_EMPLOYEE", employee });
      }
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  const updateEmployee = useCallback(
    async (id: number, data: Partial<EmployeeFormData>) => {
      try {
        await hrAPI.updateEmployee(id, data);

        // Transformer les données pour le dispatch (exclure le File)
        const { photo, ...dataForDispatch } = data;
        const employeeData: Partial<HREmployee> = {
          ...dataForDispatch,
          // Si photo est une string, on la garde, sinon on l'ignore
          ...(typeof photo === "string" ? { photo } : {}),
        };

        dispatch({ type: "UPDATE_EMPLOYEE", id, employee: employeeData });
        // Appel direct sans dépendance pour éviter les boucles
        const employees = await hrAPI.getEmployees();
        if (employees) {
          dispatch({ type: "SET_EMPLOYEES", employees });
        }
      } catch (error) {
        throw error;
      }
    },
    [] // Supprimé fetchEmployees des dépendances
  );

  const deleteEmployee = useCallback(async (id: number) => {
    try {
      await hrAPI.deleteEmployee(id);
      dispatch({ type: "REMOVE_EMPLOYEE", id });
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  // Départements
  const createDepartment = useCallback(async (data: DepartmentFormData) => {
    try {
      const department = await hrAPI.createDepartment(data);
      if (department) {
        dispatch({ type: "ADD_DEPARTMENT", department });
      }
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  const updateDepartment = useCallback(
    async (id: number, data: Partial<DepartmentFormData>) => {
      try {
        await hrAPI.updateDepartment(id, data);
        dispatch({ type: "UPDATE_DEPARTMENT", id, department: data });
        // Appel direct sans dépendance
        const departments = await hrAPI.getDepartments();
        if (departments) {
          dispatch({ type: "SET_DEPARTMENTS", departments });
        }
      } catch (error) {
        throw error;
      }
    },
    [] // Supprimé fetchDepartments des dépendances
  );

  const deleteDepartment = useCallback(async (id: number) => {
    try {
      await hrAPI.deleteDepartment(id);
      dispatch({ type: "REMOVE_DEPARTMENT", id });
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  // Postes
  const createPosition = useCallback(async (data: PositionFormData) => {
    try {
      const position = await hrAPI.createPosition(data);
      if (position) {
        dispatch({ type: "ADD_POSITION", position });
      }
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  const updatePosition = useCallback(
    async (id: number, data: Partial<PositionFormData>) => {
      try {
        await hrAPI.updatePosition(id, data);
        dispatch({ type: "UPDATE_POSITION", id, position: data });
        // Appel direct sans dépendance
        const positions = await hrAPI.getPositions();
        if (positions) {
          dispatch({ type: "SET_POSITIONS", positions });
        }
      } catch (error) {
        throw error;
      }
    },
    [] // Supprimé fetchPositions des dépendances
  );

  const deletePosition = useCallback(async (id: number) => {
    try {
      await hrAPI.deletePosition(id);
      dispatch({ type: "REMOVE_POSITION", id });
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  // Contrats
  const createContract = useCallback(async (data: ContractFormData) => {
    try {
      const contract = await hrAPI.createContract(data);
      if (contract) {
        dispatch({ type: "ADD_CONTRACT", contract });
      }
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  const updateContract = useCallback(
    async (id: number, data: Partial<ContractFormData>) => {
      try {
        await hrAPI.updateContract(id, data);
        // Pas de dispatch intermédiaire car on recharge toutes les données après
        // Appel direct sans dépendance
        const contracts = await hrAPI.getContracts();
        if (contracts) {
          dispatch({ type: "SET_CONTRACTS", contracts });
        }
      } catch (error) {
        throw error;
      }
    },
    [] // Supprimé fetchContracts des dépendances
  );

  const deleteContract = useCallback(async (id: number) => {
    try {
      await hrAPI.deleteContract(id);
      dispatch({ type: "REMOVE_CONTRACT", id });
    } catch (error) {
      throw error;
    }
  }, []); // Pas de dépendances

  // ===== UTILITY FUNCTIONS =====

  const refreshAll = useCallback(async () => {
    // Appels directs sans dépendances pour éviter les boucles
    await Promise.all([
      (async () => {
        const employees = await hrAPI.getEmployees();
        if (employees) dispatch({ type: "SET_EMPLOYEES", employees });
      })(),
      (async () => {
        const departments = await hrAPI.getDepartments();
        if (departments) dispatch({ type: "SET_DEPARTMENTS", departments });
      })(),
      (async () => {
        const positions = await hrAPI.getPositions();
        if (positions) dispatch({ type: "SET_POSITIONS", positions });
      })(),
      (async () => {
        const contracts = await hrAPI.getContracts();
        if (contracts) dispatch({ type: "SET_CONTRACTS", contracts });
      })(),
    ]);
  }, []); // Supprimé toutes les dépendances

  const clearErrors = useCallback(() => {
    dispatch({ type: "CLEAR_ERRORS" });
  }, []);

  // ===== LOAD INITIAL DATA =====
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          fetchEmployees(),
          fetchDepartments(),
          fetchPositions(),
          fetchContracts(),
        ]);
      } catch (error) {
      }
    };

    loadInitialData();
  }, []); // Supprimé toutes les dépendances fetchEmployees etc

  // ===== MEMOIZED VALUE =====
  const value = useMemo(
    () => ({
      ...state,
      fetchEmployees,
      fetchDepartments,
      fetchPositions,
      fetchContracts,
      createEmployee,
      updateEmployee,
      deleteEmployee,
      createDepartment,
      updateDepartment,
      deleteDepartment,
      createPosition,
      updatePosition,
      deletePosition,
      createContract,
      updateContract,
      deleteContract,
      refreshAll,
      clearErrors,
    }),
    [
      state,
      fetchEmployees,
      fetchDepartments,
      fetchPositions,
      fetchContracts,
      createEmployee,
      updateEmployee,
      deleteEmployee,
      createDepartment,
      updateDepartment,
      deleteDepartment,
      createPosition,
      updatePosition,
      deletePosition,
      createContract,
      updateContract,
      deleteContract,
      refreshAll,
      clearErrors,
    ]
  );

  return <HRContext.Provider value={value}>{children}</HRContext.Provider>;
};

export const useHR = (): HRContextType => {
  const context = useContext(HRContext);
  if (context === undefined) {
    throw new Error("useHR must be used within an HRProvider");
  }
  return context;
};
